import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AgentError, AgentGateway } from '../../../gateways/agent.gateway';
import { Agent, runtimeLabel } from '../../../models/agent.model';

type DetailState = 'loading' | 'ready' | 'not-found' | 'error';

@Component({
    selector: 'app-agent-detail',
    standalone: true,
    imports: [CommonModule, RouterLink],
    template: `
        <div class="page-content">
            @if (state() === 'loading') {
                <div class="state" role="status" aria-live="polite">Loading agent…</div>
            }

            @if (state() === 'not-found') {
                <div class="state" role="alert">
                    <h1>Agent not found</h1>
                    <p>This agent does not exist or has been deleted.</p>
                    <a class="btn" [routerLink]="['../../agents']">Back to agents</a>
                </div>
            }

            @if (state() === 'error') {
                <div class="state state-error" role="alert">
                    <p>{{ errorMessage() }}</p>
                    <a class="btn" [routerLink]="['../../agents']">Back to agents</a>
                </div>
            }

            @if (state() === 'ready' && agent(); as a) {
                <div class="page-header">
                    <div>
                        <h1>{{ a.name }}</h1>
                        <p>{{ a.description }}</p>
                    </div>
                    <div class="header-actions">
                        <a class="btn" [routerLink]="['../../agents', a.id, 'edit']">Edit</a>
                        <button
                            type="button"
                            class="btn btn-danger"
                            (click)="remove(a)"
                            [disabled]="deleting()"
                        >
                            {{ deleting() ? 'Deleting…' : 'Delete' }}
                        </button>
                    </div>
                </div>

                @if (rowError()) {
                    <div class="state state-error" role="alert">{{ rowError() }}</div>
                }

                <dl class="detail-grid">
                    <dt>Runtime</dt>
                    <dd>{{ runtimeLabelFor(a.runtimeId) }} ({{ a.runtimeId }})</dd>
                    <dt>Status</dt>
                    <dd>{{ a.status }}</dd>
                    <dt>Version</dt>
                    <dd>{{ a.version }}</dd>
                    <dt>Memory ID</dt>
                    <dd>{{ a.memoryId }}</dd>
                    <dt>Namespace</dt>
                    <dd>{{ a.namespace }}</dd>
                    <dt>Owner ID</dt>
                    <dd>{{ a.ownerId }}</dd>
                </dl>

                <a class="btn" [routerLink]="['../../agents']">Back to agents</a>
            }
        </div>
    `,
    styles: [
        `
            .page-content {
                padding: 2rem;
            }
            .page-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 1rem;
                margin-bottom: 1.5rem;
            }
            .header-actions {
                display: flex;
                gap: 0.5rem;
                flex-shrink: 0;
            }
            h1 {
                font-size: 1.75rem;
                font-weight: 600;
                margin-bottom: 0.5rem;
                color: #1a1a1a;
            }
            p {
                color: #666;
                margin: 0;
            }
            .state {
                padding: 2rem;
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                text-align: center;
                color: #666;
            }
            .state-error {
                border-color: #f5c2c2;
                background: #fdecea;
                color: #a12622;
                text-align: left;
            }
            .detail-grid {
                display: grid;
                grid-template-columns: 160px 1fr;
                gap: 0.5rem 1rem;
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                padding: 1.5rem;
                margin-bottom: 1.5rem;
            }
            dt {
                font-weight: 600;
                color: #445;
            }
            dd {
                margin: 0;
                color: #1a1a1a;
                word-break: break-word;
            }
            .btn {
                display: inline-block;
                padding: 0.5rem 0.9rem;
                border-radius: 6px;
                border: 1px solid #d0d0d0;
                background: white;
                color: #1a1a1a;
                text-decoration: none;
                cursor: pointer;
                font-size: 0.9rem;
            }
            .btn:hover {
                background: #f5f5f5;
            }
            .btn-danger {
                border-color: #e0b4b4;
                color: #a12622;
            }
            .btn-danger:hover {
                background: #fdecea;
            }
            .btn:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
        `,
    ],
})
export class AgentDetail implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly gateway = inject(AgentGateway);

    readonly state = signal<DetailState>('loading');
    readonly agent = signal<Agent | null>(null);
    readonly errorMessage = signal('');
    readonly rowError = signal('');
    readonly deleting = signal(false);

    private projectId = '';
    private agentId = '';

    ngOnInit(): void {
        this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
        this.agentId = this.route.snapshot.paramMap.get('agentId') ?? '';
        this.load();
    }

    load(): void {
        this.state.set('loading');
        this.gateway.getAgent(this.projectId, this.agentId).subscribe({
            next: (agent) => {
                this.agent.set(agent);
                this.state.set('ready');
            },
            error: (err: Error) => {
                if (this.isNotFound(err)) {
                    this.state.set('not-found');
                } else {
                    this.errorMessage.set(err.message);
                    this.state.set('error');
                }
            },
        });
    }

    private isNotFound(err: Error): boolean {
        return err instanceof AgentError && (err.kind === 'not-found' || err.status === 404);
    }

    runtimeLabelFor(id: string): string {
        return runtimeLabel(id);
    }

    remove(agent: Agent): void {
        if (!confirm(`Delete "${agent.name}"? This cannot be undone.`)) {
            return;
        }
        this.deleting.set(true);
        this.rowError.set('');
        this.gateway.deleteAgent(this.projectId, agent.id).subscribe({
            next: () => {
                this.router.navigate(['../../agents'], { relativeTo: this.route });
            },
            error: (err: Error) => {
                this.deleting.set(false);
                this.rowError.set(err.message);
            },
        });
    }
}
